import { MutableRefObject } from 'react';
import qs from 'query-string';
import { RouteComponentProps } from 'react-router-dom';
import analytics from '../../services/analytics';
import elasticSearch from '../../services/search-api/elastic-search';
import searchStateToElasticParams from '../../utils/base-search/search-state-to-elastic-params';
import cookiesManager from '../../utils/browser-storage/cookies';
import findWhere from '../../utils/find-where';
import pubsub from '../../utils/pubsub';
import scrollTo from '../../utils/scroll-to';
import session from '../../utils/session';
import {
    DefaultFiltersStateType,
    FORMAT_FILTER_IDS,
    MATERIAL_IDS_START_RANGE,
    MULTIPLE_SELECTION_FILTERS,
    SEARCH_KEYS,
    analyticsAction,
    filtersList,
    moreItemsLimit
} from './base-search-result.constants';

export const publishQueryEvent = (query: string) => {
    pubsub.publish('query', query);
};

export const parseURL = ({
    limit,
    authorId,
    page,
    defaultSort,
    preselectedTaxonomy,
    preselectedQuery,
    preselectedInteractive
}: ParseQueryProps): SearchState => {
    const search = qs.parse(window.location.search, { arrayFormat: 'comma' });
    const searchSorting = search.query ? '' : defaultSort ?? '';

    const res: SearchState = {
        [SEARCH_KEYS.QUERY]: (typeof search.query !== 'undefined' ? search.query : preselectedQuery ?? '')
            ?.toString()
            .replace(/%sl%/g, '/'),
        [SEARCH_KEYS.PRICE]: search.prc || [],
        [SEARCH_KEYS.SALE]: search.sale || [],
        [SEARCH_KEYS.LANGUAGES]: search.lng || [],
        [SEARCH_KEYS.FORMAT]: typeof search.f !== 'undefined' ? search.f : preselectedInteractive ?? [],
        [SEARCH_KEYS.TOTAL_PAGES]: search.tp || [],
        [SEARCH_KEYS.FILE_TYPES]: search.ft || [],
        [SEARCH_KEYS.CLASS_GRADES]: search.c || [],
        [SEARCH_KEYS.LEARNING_YEARS]: search.ly || [],
        [SEARCH_KEYS.SCHOOL_TYPES]: search.st || [],
        [SEARCH_KEYS.MATERIAL_TYPES]: search.mt || [],
        [SEARCH_KEYS.TAGS]: search.tg || [],
        [SEARCH_KEYS.FOLDER]: search.folder || '',
        [SEARCH_KEYS.PAGE]: page,
        [SEARCH_KEYS.TAXONOMY]: typeof search.t !== 'undefined' ? search.t : preselectedTaxonomy ?? '',
        [SEARCH_KEYS.OWNED_BY]: search.ob || '',
        [SEARCH_KEYS.SORTING]: typeof search.s === 'undefined' ? searchSorting : search.s,
        [SEARCH_KEYS.PREVENT_AUTOSUGGEST]: search.pas,
        author: authorId ?? '',
        limit
    } as unknown as SearchState;

    Object.keys(res).forEach((key) => {
        if (MULTIPLE_SELECTION_FILTERS.indexOf(key) !== -1) {
            // @ts-ignore
            if (!Array.isArray(res[key]) && res[key].length) {
                // @ts-ignore
                res[key] = [res[key]];
            }
        }
    });

    return res;
};

export const isEmptySearch = (search = {} as UrlSearchParams) => {
    const noQuery = !search.query;
    const searchValues = Object.values(search).filter((item) => item);
    const noFilters = searchValues.length === 0 || (search.p && searchValues.length === 1);

    return noFilters && noQuery;
};

export const isSearchById = (search = {} as UrlSearchParams, materialIdsRange = MATERIAL_IDS_START_RANGE) => {
    const query = (search.query || '').trim();
    let queryIsId = false;
    const queryToNumber = window.parseInt(query);

    if (queryToNumber && typeof queryToNumber === 'number' && queryToNumber >= materialIdsRange) {
        queryIsId = true;
    }

    return queryIsId;
};

export const sendAnalytics = (eventAction: string, eventLabel: string) => {
    analytics.push({
        event: 'gtmEvent',
        eventAction,
        eventCategory: 'mkt-userInteraction',
        eventLabel,
        eventNonInteraction: false
    });
};

export const detectumIdentifierInfo = () => {
    const GAClientId = cookiesManager.getItem('_ga');
    const userId = window.storage.getItem('userId');

    if (session.isLoggedIn() && userId && GAClientId) {
        return `${userId}:${GAClientId}`;
    }
};

export const processFormatFilterCombination = (state: SearchState): SearchState => {
    const format = state[SEARCH_KEYS.FORMAT] || [];

    const interactiveSelected = format.some((id) => Number(id) === FORMAT_FILTER_IDS.INTERACTIVE);
    const downloadableSelected = format.some((id) => Number(id) === FORMAT_FILTER_IDS.DOWNLOADABLE);

    if (!interactiveSelected) {
        state[SEARCH_KEYS.FORMAT] = format
            .filter((id) => Number(id) !== FORMAT_FILTER_IDS.INTERACTIVE_PURCHASED_CUSTOMIZED)
            .filter((id) => Number(id) !== FORMAT_FILTER_IDS.INTERACTIVE_SELF_CREATED)
            .filter((id) => Number(id) !== FORMAT_FILTER_IDS.INTERACTIVE_PURCHASED)
            .filter((id) => Number(id) !== FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE)
            .filter((id) => Number(id) !== FORMAT_FILTER_IDS.INTERACTIVE_HYBRID);
    }

    if (!downloadableSelected) {
        state[SEARCH_KEYS.FILE_TYPES] = [];
    }

    return state;
};

export const shortItemsSortFunc: SortFunc = (a, b) => b.total - a.total;

export const allItemsSortFunc: SortFunc = (a, b) => (a.title > b.title ? 1 : a.title < b.title ? -1 : 0);

type ChangeUrlPropsType = {
    sKey: string;
    location: RouteComponentProps['location'];
    history: RouteComponentProps['history'];
    refs: MutableRefObject<RefsType>;
};

export const changeUrlHelper = ({ sKey, history, location, refs }: ChangeUrlPropsType) => {
    const currentSearch: Partial<SearchState> = qs.parse(window.location.search, {
        arrayFormat: 'comma',
        parseNumbers: true
    }) as SearchState;

    refs.current.appliedFilters.forEach((f) => {
        currentSearch[f] = refs.current.search[f];
    });
    currentSearch[sKey] = refs.current.search[sKey];
    currentSearch[SEARCH_KEYS.FORMAT] = refs.current.search[SEARCH_KEYS.FORMAT];
    // currentSearch[SEARCH_KEYS.OWNED_BY] = refs.current.search[SEARCH_KEYS.OWNED_BY];
    currentSearch.query = refs.current.search.query;
    currentSearch.p = refs.current.search.p;

    if (currentSearch.p === 1) {
        delete currentSearch.p;
    }
    processFormatFilterCombination(currentSearch as SearchState);
    publishQueryEvent(refs.current.search.query);
    const url = `${location.pathname}?${qs.stringify(currentSearch, {
        arrayFormat: 'comma',
        parseNumbers: true
    } as Object)}`;

    if (sKey === 'query') {
        window.location.href = url;
    } else {
        history.push(url);
    }
};

type getFiltersPropsType = {
    response: MaterialsSearchResolveDataType;
    refs: MutableRefObject<RefsType>;
};

export const getFiltersHelper = ({ response, refs }: getFiltersPropsType) => {
    const selectedFilters: SelectedFiltersData[] = [];

    if (refs.current.search.query) {
        selectedFilters.push({
            icon: 'search',
            // @ts-ignore
            id: 'search',
            sKey: 'query',
            title: `"${refs.current.search.query}"`
        });
    }

    const suggestionFilterTitles: string[] = [];

    const { filters, taxonomyFilters, taxonomyParents, autoSuggest, suggestion } = response.data.items;

    refs.current.appliedFilters = [];
    Object.keys(filters || {}).forEach((k) => {
        const fList = filtersList().filter((f) => f.dataKey === k);

        if (fList.length) {
            const sKey = fList[0].props.searchKey as string;
            const appliedFilters = autoSuggest?.autoAppliedFilters[fList[0].dataKey as string];
            let value = refs.current.search[sKey];

            fList[0].props.expanded = false;

            if (appliedFilters) {
                suggestionFilterTitles.push(fList[0].filterTitle as string);
                appliedFilters.forEach((f: string) => {
                    const newVal = [...refs.current.search[sKey], `${f}`];

                    refs.current.search[sKey] = newVal;
                    refs.current.appliedFilters.push(sKey);
                    value = newVal;
                });
                refs.current.search.query = autoSuggest?.query;
            }

            if (value.length) {
                if (fList[0].props.shortItemsSortFunc && filters[k].length) {
                    filters[k].sort(fList[0].props.shortItemsSortFunc);
                }

                filters[k].forEach((f: SelectedFiltersData, i: number) => {
                    if (value.indexOf(`${f.id}`) !== -1 || value.indexOf(f.id) !== -1) {
                        /* eslint-disable */
                        f.isChecked = true;
                        f.sKey = sKey;

                        if (i >= moreItemsLimit) {
                            fList[0].props.expanded = true;
                        }
                        /* eslint-enable */
                        selectedFilters.push(f);
                    }
                });
            }
        }
    });

    if (refs.current.search.f?.length) {
        filters.format.forEach((filter) => {
            if (refs.current.search.f.some((id: string | number) => Number(id) === Number(filter.id))) {
                filter.isChecked = true;
                filter.sKey = 'f';
                selectedFilters.push(filter);
            }
        });
    }

    if (refs.current.search.ob?.length) {
        filters.ownedBy?.forEach((filter) => {
            if (refs.current.search.ob.some((id: string | number) => Number(id) === Number(filter.id))) {
                filter.isChecked = true;
                filter.sKey = 'ob';
                selectedFilters.push(filter);
            }
        });
    }

    if (refs.current.search.ft?.length) {
        filters.file_types.forEach((filter) => {
            if (refs.current.search.ft.some((item) => item === String(filter.id))) {
                filter.isChecked = true;
                filter.sKey = 'ft';
                selectedFilters.push(filter);
            }
        });
    }

    // Special logic for sale
    if (refs.current.search.sale.length) {
        const sale = filters.price_sale[0];

        sale.isChecked = true;
        sale.sKey = SEARCH_KEYS.SALE;
        selectedFilters.push(sale);
    }

    if (filters?.price_sale?.length) {
        const isSellerFilter = filters.price.filter((item) => item.isSaleFilter);

        if (!isSellerFilter.length) {
            filters.price.push({ ...filters.price_sale[0], isSaleFilter: true } as Filter);
        }
    }

    if (refs.current.search.t && taxonomyParents?.length) {
        selectedFilters.push({ id: taxonomyFilters[0].id as number, sKey: 't', title: taxonomyFilters[0].title });
    }

    if (suggestionFilterTitles.length) {
        publishQueryEvent(refs.current.search.query);

        if (refs.current.search.query) {
            selectedFilters[0].title = `"${refs.current.search.query}"`;
        } else {
            selectedFilters.shift();
        }
    }

    //@ts-ignore
    const newFiltersData: DefaultFiltersStateType = {
        taxonomyFilters,
        taxonomyParents,
        ...filters
    };

    return {
        newFiltersData,
        selectedFilters,
        suggestion,
        autoSuggest,
        suggestionFilterTitles
    };
};

export const elasticSearchHelper = async ({ response, refs }: getFiltersPropsType) => {
    const r: MaterialsSearchResolveDataType = await elasticSearch.get(
        searchStateToElasticParams({
            ...refs.current.search,
            f: [FORMAT_FILTER_IDS.INTERACTIVE_STANDALONE] as unknown as string[]
        })
    );

    if (r.data.total) {
        r.data.items.materials.sort(
            (a, b) => (b?.unit?.usage?.studentCount ?? 0) - (a?.unit?.usage?.studentCount ?? 0)
        );

        const mostUsed = r.data.items.materials.filter((m) => (m?.unit?.usage?.studentCount ?? 0) > 24);

        if (mostUsed.length) {
            let i = 0;

            while (i !== mostUsed.length) {
                // eslint-disable-next-line @typescript-eslint/no-loop-func
                if (response.data.items.materials.some((m) => m.id === mostUsed[i].id)) {
                    i++;
                } else {
                    response.data.items.materials = [mostUsed[i], ...response.data.items.materials];
                    i = mostUsed.length;
                }
            }
        }
    }
};

export const onFilterHelper = (
    item: Filter,
    key: string,
    refs: MutableRefObject<RefsType>,
    scrollToTopBlock: boolean
): string => {
    const searchKey = item.isSaleFilter ? SEARCH_KEYS.SALE : key;

    refs.current.search.p = 1;

    if (!Array.isArray(refs.current.search[searchKey])) {
        refs.current.search[searchKey] = [];
    }

    refs.current.search[searchKey] = !item.isChecked
        ? [...refs.current.search[searchKey], item.id]
        : refs.current.search[searchKey].filter((id: number) => `${id}` !== `${item.id}`);

    if (!item.isChecked) {
        sendAnalytics(analyticsAction[searchKey], item.title);
    }

    if (!scrollToTopBlock) {
        scrollTo(0);
    }

    return searchKey;
};

export const onListItemsChangedHelper = (items: Partial<Material>[], materials: Material[]): Material[] => {
    const list: Material[] = [];

    materials.forEach((material) => {
        const result = findWhere(items, {
            id: material.id
        });

        if (result) {
            list.push({
                ...material,
                ...result
            });
        } else {
            list.push(material);
        }
    });

    return list;
};

export const onRemoveFilterHelper = (filter: SelectedFiltersData, refs: MutableRefObject<RefsType>) => {
    if (Array.isArray(refs.current.search[filter.sKey])) {
        refs.current.search[filter.sKey] = refs.current.search[filter.sKey].filter((k: string) => `${filter.id}` !== k);
    } else {
        refs.current.search[filter.sKey] = '';
    }

    refs.current.search.p = 1;
    refs.current.search.author = ''; // remove filter by author when other are deleted, because we don't show author options on this page. But author filter still can be applied through url params

    return filter.sKey;
};
